import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  ListMusic, 
  Plus, 
  Play, 
  Heart,
  Trash2,
  Globe,
  Lock,
  Music,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { isSinger } from '../utils/singerFriendly';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MedleysPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const singerMode = isSinger(user);
  const navigate = useNavigate();
  const [myMedleys, setMyMedleys] = useState([]);
  const [publicMedleys, setPublicMedleys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newMedley, setNewMedley] = useState({ title: '', is_public: true });

  useEffect(() => {
    fetchMedleys();
  }, []);

  const fetchMedleys = async () => {
    try {
      const [myRes, publicRes] = await Promise.all([
        fetch(`${API}/medleys/my`, {
          credentials: 'include',
          headers: getAuthHeaders()
        }),
        fetch(`${API}/medleys?public_only=true&limit=20`, {
          credentials: 'include',
          headers: getAuthHeaders()
        })
      ]);
      
      if (myRes.ok) setMyMedleys(await myRes.json());
      if (publicRes.ok) setPublicMedleys(await publicRes.json());
    } catch (error) {
      console.error('Error fetching medleys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMedley = async (medleyId) => {
    if (!window.confirm('¿Eliminar este medley?')) return;
    
    try {
      const response = await fetch(`${API}/medleys/${medleyId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Medley eliminado');
        setMyMedleys(prev => prev.filter(m => m.medley_id !== medleyId));
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getKeyBadgeClass = (key) => {
    const keyMap = {
      'C': 'bg-key-c', 'G': 'bg-key-g', 'D': 'bg-key-d',
      'A': 'bg-key-a', 'E': 'bg-key-e', 'B': 'bg-key-b',
      'F#': 'bg-key-fsharp', 'Db': 'bg-key-db', 'Ab': 'bg-key-ab',
      'Eb': 'bg-key-eb', 'Bb': 'bg-key-bb', 'F': 'bg-key-f'
    };
    const baseKey = key?.replace('m', '');
    return keyMap[baseKey] || 'bg-primary';
  };

  const MedleyCard = ({ medley, showDelete = false }) => (
    <Card className="border-border/50 hover:shadow-md transition-all duration-300 hover:-translate-y-1" data-testid={`medley-card-${medley.medley_id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            {!singerMode && medley.global_key && (
              <Badge className={getKeyBadgeClass(medley.global_key)}>
                {medley.global_key}
              </Badge>
            )}
            {medley.is_public ? (
              <Globe className="w-4 h-4 text-muted-foreground" />
            ) : (
              <Lock className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <Heart className="w-4 h-4" />
            <span className="text-sm">{medley.likes}</span>
          </div>
        </div>
        
        <Link to={`/medleys/${medley.medley_id}`}>
          <h3 className="font-heading font-semibold text-lg mb-2 hover:text-primary transition-colors">
            {medley.title}
          </h3>
        </Link>
        
        <p className="text-sm text-muted-foreground mb-3">
          Por {medley.author_name} • {medley.songs?.length || 0} canciones
        </p>
        
        <div className="flex flex-wrap gap-1 mb-4">
          {medley.songs?.slice(0, 3).map((song, idx) => (
            <Badge key={idx} variant="outline" className="text-xs">
              {song.title}
            </Badge>
          ))}
          {(medley.songs?.length || 0) > 3 && (
            <Badge variant="outline" className="text-xs">
              +{medley.songs.length - 3}
            </Badge>
          )}
        </div>
        
        <div className="flex gap-2">
          <Link to={`/singer?medley=${medley.medley_id}`} className="flex-1">
            <Button variant="outline" size="sm" className="w-full gap-1">
              <Play className="w-4 h-4" />
              Presentar
            </Button>
          </Link>
          {showDelete && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive"
              onClick={() => handleDeleteMedley(medley.medley_id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-8 animate-fade-in" data-testid="medleys-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Mis Medleys</h1>
          <p className="text-muted-foreground">Gestiona tus popurrís y medleys</p>
        </div>
        <Link to="/generator">
          <Button className="gap-2" data-testid="create-medley-btn">
            <Plus className="w-4 h-4" />
            Crear con IA
          </Button>
        </Link>
      </div>

      {/* My Medleys */}
      <section>
        <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
          <ListMusic className="w-5 h-5" />
          Mis Medleys ({myMedleys.length})
        </h2>
        
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-muted rounded-xl animate-pulse" />
            ))}
          </div>
        ) : myMedleys.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myMedleys.map(medley => (
              <MedleyCard key={medley.medley_id} medley={medley} showDelete />
            ))}
          </div>
        ) : (
          <Card className="border-border/50">
            <CardContent className="py-12 text-center">
              <ListMusic className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <h3 className="font-medium mb-2">No tienes medleys aún</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Crea tu primer medley con la ayuda de la IA
              </p>
              <Link to="/generator">
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear mi primer medley
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Public Medleys */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Medleys Públicos
          </h2>
          <Link to="/community">
            <Button variant="ghost" size="sm" className="gap-1">
              Ver más
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
        
        {publicMedleys.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {publicMedleys.slice(0, 6).map(medley => (
              <MedleyCard key={medley.medley_id} medley={medley} />
            ))}
          </div>
        ) : (
          <Card className="border-border/50">
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">No hay medleys públicos aún</p>
            </CardContent>
          </Card>
        )}
      </section>
    </div>
  );
};

export default MedleysPage;
